import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  XCircle,
  FileCode2,
  FileText,
  ChevronDown,
  ChevronUp,
  Copy,
  MessageCircle,
  Lightbulb,
  Terminal,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from '@/providers/org-provider';
import { useAuth } from '@/providers/auth-provider';
import { useState, useEffect, useRef } from 'react';
import type { EducationContent } from '@nirvik/shared';
import { apiClient } from '@/lib/api-client';
import { AGENT_MAP, COLOR_MAP } from '@/lib/agent-meta';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Markdown } from '@/components/shared/markdown';
import { cn } from '@/lib/utils';
import { ReportViewer } from '@/components/report-viewer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FindingSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

interface LogEntry {
  t: string;
  l: 'i' | 'w' | 'e';
  m: string;
}

interface AgentRunUsage {
  provider?: string;
  model?: string;
  source?: 'org' | 'platform' | string;
  totalCostUsd?: number;
  totalTokens?: {
    input_tokens?: number;
    output_tokens?: number;
    cache_creation_input_tokens?: number;
    cache_read_input_tokens?: number;
  } | null;
  byModel?: Record<string, {
    inputTokens: number;
    outputTokens: number;
    cacheReadInputTokens: number;
    cacheCreationInputTokens: number;
    costUsd: number;
    sdkReportedCost: boolean;
  }>;
}

interface AgentRun {
  runId: string;
  agentType: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  progressMessage: string;
  llmModel?: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  duration: number | null;
  findingSummary: FindingSummary;
  errorMessage: string | null;
  config: Record<string, unknown>;
  logs?: LogEntry[];
  sdkCostUsd?: string | number | null;
  usage?: AgentRunUsage | null;
}

interface Finding {
  findingId: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  title: string;
  description: string;
  filePath?: string;
  lineNumber?: number;
  codeSnippet?: string;
  remediation: string;
  autoFixAvailable: boolean;
  autoFixDiff?: string;
  confidence: 'high' | 'medium' | 'low';
  cweId?: string;
  owaspCategory?: string;
  educationContent?: EducationContent | null;
  llmModel?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------


const SEVERITY_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
  medium: { bg: 'bg-yellow-500/10', text: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500' },
  low: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  info: { bg: 'bg-gray-500/10', text: 'text-muted-foreground', dot: 'bg-gray-500' },
};

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; icon: React.ElementType; animate?: boolean }
> = {
  queued: { bg: 'bg-gray-500/10', text: 'text-muted-foreground', icon: Clock },
  running: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', icon: Loader2, animate: true },
  completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  failed: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', icon: AlertTriangle },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-muted-foreground', icon: XCircle },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------


function FindingCard({ finding, orgId, portfolioId, projectId }: { finding: Finding; orgId?: string; portfolioId?: string; projectId?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [fetchedContent, setFetchedContent] = useState<EducationContent | null>(null);
  const [showEducation, setShowEducation] = useState(false);
  const [explainError, setExplainError] = useState('');
  const sev = SEVERITY_STYLES[finding.severity] ?? SEVERITY_STYLES.info;

  const education: EducationContent | null =
    fetchedContent || finding.educationContent || null;

  const handleExplain = async () => {
    if (education) {
      setShowEducation((v) => !v);
      return;
    }

    if (!orgId || !portfolioId || !projectId) return;

    setExplaining(true);
    setExplainError('');

    try {
      const result = await apiClient.explainFinding(orgId, portfolioId, projectId, finding.findingId);
      const data = result.data as EducationContent | undefined;
      if (data && data.plainLanguage) {
        setFetchedContent(data);
        setShowEducation(true);
      } else {
        setExplainError('Could not get explanation.');
      }
    } catch (err) {
      setExplainError(err instanceof Error ? err.message : 'Failed to get explanation.');
    } finally {
      setExplaining(false);
    }
  };

  return (
    <Card className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-start gap-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className={cn('h-2 w-2 rounded-full mt-2 flex-shrink-0', sev.dot)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize', sev.bg, sev.text)}>
              {finding.severity}
            </span>
            <span className="text-xs text-muted-foreground">{finding.category}</span>
            {finding.cweId && <span className="text-xs text-muted-foreground">{finding.cweId}</span>}
            {finding.confidence && (
              <span className="text-xs text-muted-foreground">
                {finding.confidence} confidence
              </span>
            )}
            {finding.llmModel && (
              <span className="text-xs text-muted-foreground font-mono">
                {finding.llmModel}
              </span>
            )}
          </div>
          <h3 className="text-sm font-medium text-foreground mt-1">{finding.title}</h3>
          {finding.filePath && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <FileCode2 className="h-3 w-3" />
              {finding.filePath}
              {finding.lineNumber && `:${finding.lineNumber}`}
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-4 space-y-4">
          {/* Explain button - top of expanded content */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExplain}
              disabled={explaining}
              className={cn(
                'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors',
                showEducation && education
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                  : 'bg-muted text-purple-700 dark:text-purple-400 hover:bg-muted hover:text-purple-800 dark:hover:text-purple-300',
                explaining && 'opacity-60 cursor-not-allowed'
              )}
            >
              {explaining ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <MessageCircle className="h-3.5 w-3.5" />
              )}
              {explaining
                ? 'Explaining...'
                : showEducation && education
                  ? 'Hide explanation'
                  : education
                    ? 'Show explanation'
                    : 'Explain this finding'}
            </button>

            {explainError && (
              <span className="text-xs text-red-700 dark:text-red-400">{explainError}</span>
            )}
          </div>

          {/* Unified education content */}
          {showEducation && education && (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-5 space-y-4">
              <div className="flex items-center gap-2 text-purple-300">
                <Lightbulb className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-wide">AI Explanation</span>
                {education.labels && education.labels.length > 0 && (
                  <div className="ml-auto flex flex-wrap gap-1">
                    {education.labels.map((label, i) => (
                      <span
                        key={i}
                        className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-sm text-foreground">{education.plainLanguage}</p>

              {education.analogy && (
                <div className="bg-muted/50 rounded-md p-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Think of it like...</p>
                  <p className="text-sm text-foreground italic">{education.analogy}</p>
                </div>
              )}

              {education.whyItMatters && (
                <div>
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-1">Why it matters</p>
                  <p className="text-sm text-foreground">{education.whyItMatters}</p>
                </div>
              )}

              {education.risk && (
                <div>
                  <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-1">What could happen</p>
                  <p className="text-sm text-foreground">{education.risk}</p>
                </div>
              )}

              {education.howToFix && (
                <div>
                  <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-2">How to fix it</p>
                  <Markdown className="text-sm text-foreground">{education.howToFix}</Markdown>
                </div>
              )}

              {education.learnMore && education.learnMore.length > 0 && (
                <div className="pt-2 border-t border-purple-500/20">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Learn more</p>
                  <ul className="space-y-1">
                    {education.learnMore.map((link, i) => (
                      <li key={i}>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-purple-300 hover:underline"
                        >
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</h4>
            <Markdown className="text-sm text-foreground">{finding.description}</Markdown>
          </div>

          {/* Code snippet */}
          {finding.codeSnippet && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Vulnerable Code</h4>
                <button
                  onClick={() => navigator.clipboard.writeText(finding.codeSnippet!)}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <pre className="bg-background border border-border rounded-lg p-3 text-xs text-foreground overflow-x-auto font-mono">
                {finding.codeSnippet}
              </pre>
            </div>
          )}

          {/* Remediation */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">How to Fix</h4>
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
              <Markdown className="text-sm text-foreground">{finding.remediation}</Markdown>
            </div>
          </div>

          {/* Auto-fix diff */}
          {finding.autoFixAvailable && finding.autoFixDiff && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Auto-Fix Patch
              </h4>
              <pre className="bg-background border border-border rounded-lg p-3 text-xs text-foreground overflow-x-auto font-mono">
                {finding.autoFixDiff}
              </pre>
            </div>
          )}

          {/* OWASP link */}
          {finding.owaspCategory && (
            <p className="text-xs text-muted-foreground">
              OWASP: {finding.owaspCategory}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// LogsPanel
// ---------------------------------------------------------------------------

const LOG_LEVEL_STYLE: Record<string, string> = {
  i: 'text-gray-400',
  w: 'text-yellow-400',
  e: 'text-red-400',
};

function LogsPanel({ logs, live }: { logs: LogEntry[]; live: boolean }) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll when new entries arrive and user hasn't scrolled up
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs.length, autoScroll]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  if (logs.length === 0) {
    return (
      <Card className="bg-[#0d1117] border border-border rounded-xl p-8 text-center">
        <Terminal className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          {live ? 'Waiting for container logs…' : 'No logs captured for this run.'}
        </p>
      </Card>
    );
  }

  return (
    <div className="relative">
      {live && (
        <div className="flex items-center gap-2 mb-2">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-muted-foreground">Live</span>
          {!autoScroll && (
            <button
              onClick={() => { setAutoScroll(true); bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
              className="ml-auto text-xs text-blue-400 hover:text-blue-300"
            >
              ↓ Jump to bottom
            </button>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="bg-[#0d1117] border border-border rounded-xl p-4 h-[480px] overflow-y-auto font-mono text-xs leading-relaxed"
      >
        {logs.map((entry, i) => (
          <div key={i} className="flex gap-3 hover:bg-white/5 px-1 rounded">
            <span className="text-gray-600 shrink-0 select-none">
              {new Date(entry.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
            <span className={cn('shrink-0 select-none w-4', LOG_LEVEL_STYLE[entry.l] ?? 'text-gray-400')}>
              {entry.l === 'w' ? '⚠' : entry.l === 'e' ? '✖' : '·'}
            </span>
            <span className={cn('break-all whitespace-pre-wrap', LOG_LEVEL_STYLE[entry.l] ?? 'text-gray-300')}>
              {entry.m}
            </span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function AgentRunDetailPage() {
  const { orgId, portfolioId, projectId, runId } = useParams<{ orgId: string; portfolioId: string; projectId: string; runId: string }>();
  const queryClient = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'findings' | 'report' | 'logs'>('findings');
  const enabled = !!orgId && !!portfolioId && !!projectId && !!runId;
  const basePath = `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`;

  const { data: runRes, isLoading: runLoading } = useQuery({
    queryKey: ['agent-run', orgId, portfolioId, projectId, runId],
    queryFn: () => apiClient.get(`${basePath}/agents/runs/${runId}`),
    enabled,
    refetchInterval: (query) => {
      const run = (query.state.data?.data as { run?: AgentRun })?.run;
      return run?.status === 'running' || run?.status === 'queued' ? 3000 : false;
    },
  });

  const run = (runRes?.data as { run?: AgentRun })?.run;

  const { data: findingsRes, isLoading: findingsLoading, refetch: refetchFindings } = useQuery({
    queryKey: ['agent-run-findings', orgId, portfolioId, projectId, runId],
    queryFn: () => apiClient.get(`${basePath}/findings?runId=${runId}`),
    enabled,
  });

  const { data: reportRes, refetch: refetchReport } = useQuery({
    queryKey: ['agent-run-report', orgId, portfolioId, projectId, runId],
    queryFn: () => apiClient.get(`${basePath}/agents/runs/${runId}/report`),
    enabled: enabled && run?.status === 'completed',
  });

  // When run transitions to completed, refresh findings + report immediately.
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = run?.status;
    if (prev !== undefined && prev !== 'completed' && curr === 'completed') {
      refetchFindings();
      refetchReport();
      setActiveTab('findings');
    }
    prevStatusRef.current = curr;
  }, [run?.status, refetchFindings, refetchReport]);

  const cancelRun = useMutation({
    mutationFn: () => apiClient.post(`${basePath}/agents/runs/${runId}/cancel`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent-run', orgId, portfolioId, projectId, runId] }),
  });

  // Delete is restricted to org admins / platform admins; the API
  // re-checks server-side. Cascade-deletes every finding produced by
  // this run, recomputes project rollups, and audit-logs.
  const navigate = useNavigate();
  const { currentOrg } = useOrgContext();
  const { user } = useAuth();
  const canDelete = (currentOrg?.role === 'admin') || !!user?.isAdmin;
  const deleteRun = useMutation({
    mutationFn: () => apiClient.deleteAgentRun(orgId!, portfolioId!, projectId!, runId!),
    onSuccess: (res) => {
      const data = res.data as { deletedFindings?: number } | undefined;
      toast.success(
        data?.deletedFindings != null && data.deletedFindings > 0
          ? `Run deleted · ${data.deletedFindings} finding${data.deletedFindings === 1 ? '' : 's'} removed`
          : 'Run deleted',
      );
      queryClient.invalidateQueries({ queryKey: ['agent-run', orgId, portfolioId, projectId] });
      queryClient.invalidateQueries({ queryKey: ['agent-run-findings', orgId, portfolioId, projectId] });
      queryClient.invalidateQueries({ queryKey: ['findings', orgId, portfolioId, projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', orgId, portfolioId, projectId] });
      navigate(`${basePath}/agents`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reportData = reportRes?.data as { report?: string } | undefined;
  const report = reportData?.report;
  const rawFindings = findingsRes?.data;
  const findings: Finding[] = Array.isArray(rawFindings)
    ? rawFindings as Finding[]
    : (rawFindings as { findings?: Finding[] })?.findings ?? [];

  const agentMeta = AGENT_MAP[run?.agentType || ''];
  const agentName = agentMeta?.name ?? run?.agentType ?? 'Unknown Agent';
  const agentDescription = agentMeta?.description;
  const AgentIcon = agentMeta?.icon ?? ShieldCheck;
  const agentColors = COLOR_MAP[agentMeta?.color ?? 'blue'];

  // Filter findings
  const filteredFindings = severityFilter
    ? findings.filter((f) => f.severity === severityFilter)
    : findings;

  // Sort: critical > high > medium > low > info
  const severityOrder = ['critical', 'high', 'medium', 'low', 'info'];
  filteredFindings.sort(
    (a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity)
  );

  if (runLoading) {
    return (
      <div className="space-y-6">
        <div className="h-4 bg-muted rounded w-32 animate-pulse" />
        <Card className="p-6 animate-pulse bg-card border-border">
          <div className="h-6 bg-muted rounded w-64 mb-3" />
          <div className="h-4 bg-muted rounded w-48" />
        </Card>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Run not found</p>
        <Link to={`${basePath}/agents`} className="text-blue-700 dark:text-blue-400 hover:underline mt-4 inline-block">
          Back to Agents
        </Link>
      </div>
    );
  }

  const st = STATUS_STYLES[run.status] ?? STATUS_STYLES.queued;
  const StatusIcon = st.icon;

  return (
    <div className="space-y-6">
      {/* Run header — stacks on phones. Inner groups use flex-wrap so the
       * status badge, the 4 metadata chips (start / complete / duration /
       * model), and the Cancel button never push horizontal overflow.
       * The model name can be very long (e.g. a HF repo ID); it truncates
       * via `max-w-full truncate` inside its chip. */}
      <Card className="bg-card border border-border rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className={cn('h-12 w-12 rounded-xl flex items-center justify-center shrink-0', st.bg)}>
              <StatusIcon className={cn('h-6 w-6', st.text, st.animate && 'animate-spin')} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center shrink-0', agentColors.bg)}>
                  <AgentIcon className={cn('h-4 w-4', agentColors.text)} />
                </div>
                <h1 className="text-lg font-bold text-foreground break-words">{agentName}</h1>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground">{run.agentType}</span>
                <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full capitalize', st.bg, st.text)}>
                  {run.status}
                </span>
              </div>
              {agentDescription && (
                <p className="text-sm text-muted-foreground mt-1.5">{agentDescription}</p>
              )}
              <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mt-2 text-xs text-muted-foreground">
                <span className="font-mono">Run: {run.runId}</span>
                <span>Started: {formatDate(run.startedAt)}</span>
                {run.completedAt && <span>Completed: {formatDate(run.completedAt)}</span>}
                {run.duration !== null && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {formatDuration(run.duration)}
                  </span>
                )}
                {run.llmModel && (
                  <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono truncate max-w-full">
                    {run.llmModel}
                  </span>
                )}
                {run.usage?.provider && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-700 dark:text-blue-400 font-mono">
                    {run.usage.provider}
                    {run.usage.source ? ` · ${run.usage.source}` : ''}
                  </span>
                )}
              </div>

              {run.usage && <UsageBar usage={run.usage} />}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            {(run.status === 'running' || run.status === 'queued') && (
              <Button
                variant="outline"
                onClick={() => cancelRun.mutate()}
                disabled={cancelRun.isPending}
                className="w-full sm:w-auto bg-muted hover:bg-muted text-foreground border-border"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline"
                onClick={() => {
                  if (confirm(`Delete this agent run permanently?\n\nAll findings produced by this run will also be deleted. This cannot be undone.`)) {
                    deleteRun.mutate();
                  }
                }}
                disabled={deleteRun.isPending}
                className="w-full sm:w-auto border-red-300 dark:border-red-500/40 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/10"
              >
                {deleteRun.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {(run.status === 'running' || run.status === 'queued') && (
          <div className="mt-4">
            <Progress value={run.progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">{run.progressMessage || `${run.progress}%`}</p>
          </div>
        )}

        {/* Error message */}
        {run.status === 'failed' && run.errorMessage && (
          <div className="mt-4 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{run.errorMessage}</p>
          </div>
        )}
      </Card>

      {/* Finding summary */}
      {run.status === 'completed' && run.findingSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: run.findingSummary.total, key: null },
            { label: 'Critical', value: run.findingSummary.critical, key: 'critical' },
            { label: 'High', value: run.findingSummary.high, key: 'high' },
            { label: 'Medium', value: run.findingSummary.medium, key: 'medium' },
            { label: 'Low', value: run.findingSummary.low, key: 'low' },
            { label: 'Info', value: run.findingSummary.info, key: 'info' },
          ].map(({ label, value, key }) => {
            const sev = key ? SEVERITY_STYLES[key] : { bg: 'bg-gray-500/10', text: 'text-foreground' };
            return (
              <button
                key={label}
                onClick={() => setSeverityFilter(severityFilter === key ? null : key)}
                className={cn(
                  'p-3 rounded-xl border transition-colors text-center',
                  severityFilter === key
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-border bg-card hover:border-border'
                )}
              >
                <p className={cn('text-2xl font-bold', sev.text)}>{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Tab bar — always show Logs; Findings + Report only when completed */}
      <div className="flex items-center gap-2 border-b border-border pb-1">
        {run.status === 'completed' && (
          <button
            onClick={() => setActiveTab('findings')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
              activeTab === 'findings'
                ? 'text-blue-700 dark:text-blue-400 border-b-2 border-blue-400'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Findings ({run.findingSummary.total})
          </button>
        )}
        {run.status === 'completed' && (
          <button
            onClick={() => setActiveTab('report')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5',
              activeTab === 'report'
                ? 'text-blue-700 dark:text-blue-400 border-b-2 border-blue-400'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <FileText className="h-3.5 w-3.5" />
            Report
          </button>
        )}
        <button
          onClick={() => setActiveTab('logs')}
          className={cn(
            'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5',
            activeTab === 'logs'
              ? 'text-blue-700 dark:text-blue-400 border-b-2 border-blue-400'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Terminal className="h-3.5 w-3.5" />
          Logs
          {run.logs && run.logs.length > 0 && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
              {run.logs.length}
            </span>
          )}
        </button>
      </div>

      {/* Logs panel */}
      {activeTab === 'logs' && (
        <LogsPanel
          logs={run.logs ?? []}
          live={run.status === 'running' || run.status === 'queued'}
        />
      )}

      {/* Findings list */}
      {run.status === 'completed' && activeTab === 'findings' && (
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Findings {severityFilter && `(${severityFilter})`}
          </h2>
          {findingsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-4 animate-pulse bg-card border-border">
                  <div className="h-4 bg-muted rounded w-48 mb-2" />
                  <div className="h-3 bg-muted rounded w-96" />
                </Card>
              ))}
            </div>
          ) : filteredFindings.length === 0 ? (
            <Card className="bg-card border border-border rounded-xl p-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {severityFilter ? `No ${severityFilter} findings` : 'No findings - your code looks clean!'}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredFindings.map((finding) => (
                <FindingCard key={finding.findingId} finding={finding} orgId={orgId} portfolioId={portfolioId} projectId={projectId} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report section */}
      {run.status === 'completed' && activeTab === 'report' && (
        report ? (
          <ReportViewer report={report} title={agentName + ' Report'} />
        ) : (
          <Card className="bg-card border border-border rounded-xl p-8 text-center">
            <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Report not yet available</p>
          </Card>
        )
      )}
    </div>
  );
}

// Compact usage / cost summary inline on the run header. Hidden when the
// run was started before the usage capture went live (run.usage === null).
function UsageBar({ usage }: { usage: AgentRunUsage }) {
  const total = usage.totalTokens || {};
  const inputTokens = total.input_tokens ?? 0;
  const outputTokens = total.output_tokens ?? 0;
  const cacheRead = total.cache_read_input_tokens ?? 0;
  const cacheWrite = total.cache_creation_input_tokens ?? 0;
  const cost = usage.totalCostUsd ?? 0;

  const fmt = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

  return (
    <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground font-mono bg-muted/40 dark:bg-muted/30 rounded-md px-2.5 py-1.5 w-fit">
      <span><span className="text-foreground/70">in</span> {fmt(inputTokens)}</span>
      <span><span className="text-foreground/70">out</span> {fmt(outputTokens)}</span>
      {cacheRead > 0 && <span><span className="text-foreground/70">cache·r</span> {fmt(cacheRead)}</span>}
      {cacheWrite > 0 && <span><span className="text-foreground/70">cache·w</span> {fmt(cacheWrite)}</span>}
      <span className="font-semibold text-foreground">${cost.toFixed(4)}</span>
    </div>
  );
}
